import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Package, MessageCircle, Phone, Plus, CheckCircle, Clock,
  Loader2, ChevronRight, Shirt, AlertCircle, Truck, Zap, Sparkles, Clock3,
  ChevronDown, History, ArrowRight, Star, CreditCard, XCircle, Receipt, IndianRupee,
  ScanSearch, Cog, Gift, Copy, Check, Share2, Bell
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import IndividualSidebar from '@/components/IndividualSidebar';
import IndividualBottomNav from '@/components/IndividualBottomNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ── Payment Modal (dashboard copy) ───────────────────────────────────────────
const PaymentModal = ({ order, token, onClose }) => {
  const [loading, setLoading] = useState(false);

  const handleEasebuzz = async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/payment/initiate`,
        { order_id: order.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = res.data.redirect_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not initiate payment');
      setLoading(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm rounded-3xl">
        <DialogHeader>
          <DialogTitle className="font-black text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Pay for Order</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div className="bg-slate-50 rounded-2xl px-4 py-3 flex items-center justify-between">
            <span className="text-sm font-semibold text-slate-600">Amount Due</span>
            <span className="text-xl font-black text-slate-900">₹{order.total_amount?.toFixed(2)}</span>
          </div>
          <button onClick={handleEasebuzz} disabled={loading}
            className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-bold py-3 rounded-2xl transition-all hover:shadow-lg hover:shadow-blue-200">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
            Pay via Easebuzz
          </button>
          <div className="flex items-start gap-3 bg-amber-50 border border-amber-100 rounded-2xl px-4 py-3">
            <IndianRupee className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-bold text-amber-800">Pay with Cash</p>
              <p className="text-xs text-amber-700 mt-0.5">Hand cash to the delivery boy or ask admin to mark it as paid.</p>
            </div>
          </div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="w-full py-2.5 rounded-xl border border-slate-200 text-slate-600 font-semibold text-sm hover:bg-slate-50">Close</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ── Invoice Modal (dashboard copy) ───────────────────────────────────────────
const InvoiceModal = ({ order, onClose, token: tokenProp }) => {
  const invoiceNum = order.invoice_number || `JF-INV-${order.id.slice(0, 8).toUpperCase()}`;
  const isPaid = order.payment_status === 'paid';
  const [downloading, setDownloading] = useState(false);
  const token = tokenProp || localStorage.getItem('individualToken');

  const gstTotal = order.gst_total || 0;
  const cgstTotal = order.cgst_total || 0;
  const sgstTotal = order.sgst_total || 0;
  const gstTaxableBase = order.gst_taxable_base || 0;

  const handleDownloadPDF = async () => {
    if (!isPaid) return;
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/individual/orders/${order.id}/invoice`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${invoiceNum}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download invoice');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto rounded-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between font-black" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <span className="flex items-center gap-2"><Receipt className="w-5 h-5 text-blue-600" /> Tax Invoice</span>
            {isPaid ? (
              <button
                onClick={handleDownloadPDF}
                disabled={downloading}
                className="flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:text-blue-700 border border-blue-200 rounded-xl px-3 py-1.5 hover:bg-blue-50 disabled:opacity-60"
              >
                {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <IndianRupee className="w-3.5 h-3.5" />}
                {downloading ? 'Downloading…' : 'Download PDF'}
              </button>
            ) : (
              <span className="text-xs text-slate-400 border border-slate-200 rounded-xl px-3 py-1.5">Pay to download</span>
            )}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl px-5 py-4 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-black text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>JustFold</p>
                <p className="text-blue-200 text-xs mt-0.5">Patel Nagar, Gurugram, Haryana 122001</p>
              </div>
              <span className="text-[10px] font-black text-blue-300 uppercase tracking-widest border border-blue-400 rounded-lg px-2 py-1">TAX INVOICE</span>
            </div>
            <div className="flex items-center justify-between mt-3">
              <div>
                <p className="text-[10px] text-blue-300 uppercase tracking-widest">Invoice No.</p>
                <p className="font-bold text-sm font-mono">{invoiceNum}</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] text-blue-300 uppercase tracking-widest">Date</p>
                <p className="font-bold text-sm">{format(new Date(order.order_date), 'dd MMM yyyy')}</p>
              </div>
            </div>
          </div>
          <div className="bg-slate-50 rounded-2xl px-4 py-3">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Billed To</p>
            <p className="font-bold text-slate-800 text-sm">{order.customer_name}</p>
            {order.customer_phone && <p className="text-xs text-slate-500 mt-0.5">+91 {order.customer_phone}</p>}
            {order.address_line1 && <p className="text-xs text-slate-500 mt-0.5">{[order.address_line2, order.address_line1, order.city, order.pincode].filter(Boolean).join(', ')}</p>}
          </div>
          {order.cart_items?.length > 0 && (
            <div className="bg-white border border-slate-100 rounded-2xl overflow-hidden">
              <div className="grid grid-cols-[1fr_auto_auto_auto] text-[9px] font-black text-slate-400 uppercase tracking-widest px-3 py-2 border-b border-slate-100 bg-slate-50">
                <span>Item</span><span className="text-right pr-3">Qty</span><span className="text-right pr-3">Taxable</span><span className="text-right">Total</span>
              </div>
              {order.cart_items.map((item, i) => {
                const taxable = item.total || 0;
                const totalWithGst = item.total_with_gst != null ? item.total_with_gst : taxable;
                const hasGst = item.gst_applicable;
                if (item.pending_breakdown) {
                  return (
                    <div key={i} className="px-3 py-2.5 border-b border-slate-50 last:border-0">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-slate-800 text-xs leading-snug">{item.description}</p>
                          {item.total_pcs > 0 && <p className="text-[10px] text-amber-600 mt-0.5">{item.total_pcs} pcs picked up</p>}
                        </div>
                        <span className="text-[10px] text-amber-600 bg-amber-50 border border-amber-100 px-2 py-0.5 rounded-full font-semibold whitespace-nowrap">Being assessed</span>
                      </div>
                    </div>
                  );
                }
                return (
                  <div key={i} className="px-3 py-2.5 border-b border-slate-50 last:border-0">
                    <div className="grid grid-cols-[1fr_auto_auto_auto] items-start gap-1">
                      <div>
                        <p className="font-medium text-slate-800 text-xs leading-snug">{item.description}</p>
                        {hasGst && <span className="inline-block text-[9px] bg-amber-50 text-amber-700 border border-amber-200 rounded px-1 mt-0.5">GST 18%</span>}
                      </div>
                      <p className="text-[10px] text-slate-400 text-right pr-3 pt-0.5">{item.quantity > 0 ? `${item.quantity} ${item.unit}` : '—'}</p>
                      <p className="text-[10px] text-slate-500 text-right pr-3 pt-0.5">₹{taxable.toFixed(2)}</p>
                      <p className="text-xs font-semibold text-slate-700 text-right pt-0.5">₹{totalWithGst.toFixed(2)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {gstTotal > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3">
              <p className="text-[10px] font-black text-amber-700 uppercase tracking-widest mb-2">GST Summary</p>
              <div className="space-y-1 text-xs">
                <div className="flex justify-between text-amber-700"><span>Taxable Amount</span><span>₹{gstTaxableBase.toFixed(2)}</span></div>
                <div className="flex justify-between text-amber-700"><span>CGST (9%)</span><span>₹{cgstTotal.toFixed(2)}</span></div>
                <div className="flex justify-between text-amber-700"><span>SGST (9%)</span><span>₹{sgstTotal.toFixed(2)}</span></div>
                <div className="flex justify-between font-bold text-amber-800 border-t border-amber-200 pt-1 mt-1"><span>Total GST</span><span>₹{gstTotal.toFixed(2)}</span></div>
              </div>
            </div>
          )}
          <div className="bg-slate-50 rounded-2xl px-4 py-3 space-y-2 text-sm">
            {order.subtotal != null && (
              <div className="flex justify-between text-slate-600"><span>Subtotal (pre-tax)</span><span>₹{order.subtotal?.toFixed(2)}</span></div>
            )}
            {order.coupon_discount > 0 && (
              <div className="flex justify-between text-emerald-600"><span>Coupon ({order.coupon_code})</span><span className="font-semibold">−₹{order.coupon_discount?.toFixed(2)}</span></div>
            )}
            {gstTotal > 0 && (
              <div className="flex justify-between text-amber-700"><span>GST (CGST + SGST)</span><span>₹{gstTotal.toFixed(2)}</span></div>
            )}
            <div className="flex justify-between text-slate-600">
              <span>Delivery</span><span>{(order.delivery_charge || 0) > 0 ? `₹${order.delivery_charge?.toFixed(2)}` : 'Free'}</span>
            </div>
            {order.overall_discount_amount > 0 && (
              <>
                <div className="flex justify-between text-slate-500 pt-2 border-t border-slate-200">
                  <span>Subtotal (before discount)</span><span>₹{(order.total_amount + order.overall_discount_amount).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-orange-600">
                  <span>Overall Discount ({order.overall_discount_pct?.toFixed(0)}%)</span><span className="font-semibold">−₹{order.overall_discount_amount.toFixed(2)}</span>
                </div>
              </>
            )}
            <div className="flex justify-between pt-2 border-t border-slate-200 font-black text-slate-900 text-base">
              <span>Grand Total</span><span>₹{order.total_amount?.toFixed(2)}</span>
            </div>
          </div>
          <div className={`rounded-2xl px-4 py-3 flex items-center gap-3 ${isPaid ? 'bg-emerald-50 border border-emerald-100' : 'bg-red-50 border border-red-100'}`}>
            {isPaid ? (
              <>
                <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                <div>
                  <p className="text-sm font-black text-emerald-800">Paid</p>
                  <p className="text-xs text-emerald-600 mt-0.5">
                    via {order.payment_method === 'easebuzz' ? 'Easebuzz' : 'Cash'}
                    {order.paid_at && ` · ${format(new Date(order.paid_at), 'dd MMM yyyy, h:mm a')}`}
                  </p>
                  {order.payment_txn_id && <p className="text-[10px] text-emerald-500 font-mono mt-0.5">Txn: {order.payment_txn_id}</p>}
                </div>
              </>
            ) : (
              <>
                <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                <div>
                  <p className="text-sm font-black text-red-700">Payment Pending</p>
                  <p className="text-xs text-red-500 mt-0.5">Complete payment to download your tax invoice</p>
                </div>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const STATUS_STEPS = [
  { key: 'pending',          label: 'Placed',      icon: Clock },
  { key: 'pickup_assigned',  label: 'Scheduled',   icon: Truck },
  { key: 'pickup_done',      label: 'Picked Up',   icon: Package },
  { key: 'in_process',       label: 'Inspecting',  icon: Cog },
  { key: 'process_complete', label: 'Insp. Done',  icon: CheckCircle },
  { key: 'cleaned',          label: 'Ready',       icon: Sparkles },
  { key: 'out_for_delivery', label: 'On the Way',  icon: Truck },
  { key: 'delivered',        label: 'Delivered',   icon: CheckCircle },
];
const STATUS_ORDER = ['pending', 'pickup_assigned', 'pickup_done', 'in_process', 'process_complete', 'cleaned', 'out_for_delivery', 'delivered'];
const STATUS_BADGE = {
  pending:          'bg-amber-50   text-amber-700   border-amber-200',
  pickup_assigned:  'bg-blue-50    text-blue-700    border-blue-200',
  pickup_done:      'bg-indigo-50  text-indigo-700  border-indigo-200',
  in_process:       'bg-violet-50  text-violet-700  border-violet-200',
  process_complete: 'bg-teal-50    text-teal-700    border-teal-200',
  cleaned:          'bg-emerald-50 text-emerald-700 border-emerald-200',
  out_for_delivery: 'bg-orange-50  text-orange-700  border-orange-200',
  delivered:        'bg-green-50   text-green-700   border-green-200',
  delivery_overdue: 'bg-red-50     text-red-700     border-red-200',
};

// ── Animated pct hook ────────────────────────────────────────────────────────
const useAnimatedPct = (targetPct, duration = 1300) => {
  const [animPct, setAnimPct] = useState(0);
  const raf = useRef(null);
  useEffect(() => {
    const start = performance.now();
    const animate = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setAnimPct(eased * targetPct);
      if (p < 1) raf.current = requestAnimationFrame(animate);
    };
    raf.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf.current);
  }, [targetPct, duration]);
  return animPct;
};

// ── Shared gauge SVG renderer ─────────────────────────────────────────────────
const GaugeSVG = ({ pct, label, subLabel, colorOverride }) => {
  const animPct = useAnimatedPct(pct);
  const cx = 100, cy = 100, r = 76;
  const startDeg = 150, span = 240;
  const toRad = d => (d * Math.PI) / 180;
  const pt = (deg, radius = r) => [cx + radius * Math.cos(toRad(deg)), cy + radius * Math.sin(toRad(deg))];
  const arcPath = (a1, a2, radius = r) => {
    const [x1, y1] = pt(a1, radius); const [x2, y2] = pt(a2, radius);
    const cwSpan = ((a2 - a1) + 360) % 360;
    return `M ${x1.toFixed(2)},${y1.toFixed(2)} A ${radius},${radius} 0 ${cwSpan > 180 ? 1 : 0} 1 ${x2.toFixed(2)},${y2.toFixed(2)}`;
  };
  const needleDeg = startDeg + animPct * span;
  const needleLen = r * 0.72;
  const [tx, ty] = pt(needleDeg, needleLen);
  const [b1x, b1y] = pt(needleDeg + 90, 7);
  const [b2x, b2y] = pt(needleDeg - 90, 7);
  const z1 = startDeg + span * 0.3;
  const z2 = startDeg + span * 0.6;
  const endDeg = startDeg + span;
  const color = colorOverride || (pct > 0.6 ? '#10B981' : pct > 0.3 ? '#F97316' : '#EF4444');
  const ticks = [0, 0.25, 0.5, 0.75, 1].map(t => {
    const deg = startDeg + t * span;
    const [ox, oy] = pt(deg, r + 6); const [ix, iy] = pt(deg, r - 12);
    return { ox, oy, ix, iy, major: t === 0 || t === 0.5 || t === 1 };
  });
  const [ex, ey] = pt(startDeg, r + 22);
  const [fx, fy] = pt(endDeg, r + 22);
  return (
    <svg viewBox="0 0 200 158" className="w-[200px]">
      <path d={arcPath(startDeg, z1)} fill="none" stroke="#FECACA" strokeWidth="15" strokeLinecap="butt" />
      <path d={arcPath(z1, z2)}       fill="none" stroke="#FED7AA" strokeWidth="15" strokeLinecap="butt" />
      <path d={arcPath(z2, endDeg)}   fill="none" stroke="#A7F3D0" strokeWidth="15" strokeLinecap="butt" />
      {animPct > 0.005 && (
        <path d={arcPath(startDeg, needleDeg)} fill="none" stroke={color} strokeWidth="15" strokeLinecap="round" />
      )}
      {ticks.map(({ ox, oy, ix, iy, major }, i) => (
        <line key={i} x1={ox.toFixed(2)} y1={oy.toFixed(2)} x2={ix.toFixed(2)} y2={iy.toFixed(2)}
          stroke="#94a3b8" strokeWidth={major ? 2 : 1.5} strokeLinecap="round" />
      ))}
      <text x={ex.toFixed(1)} y={ey.toFixed(1)} textAnchor="middle" fontSize="10" fontWeight="800" fill="#ef4444" fontFamily="Manrope,sans-serif">E</text>
      <text x={fx.toFixed(1)} y={fy.toFixed(1)} textAnchor="middle" fontSize="10" fontWeight="800" fill="#10b981" fontFamily="Manrope,sans-serif">F</text>
      <polygon points={`${tx.toFixed(2)},${ty.toFixed(2)} ${b1x.toFixed(2)},${b1y.toFixed(2)} ${b2x.toFixed(2)},${b2y.toFixed(2)}`} fill="#1e293b" />
      <circle cx={cx} cy={cy} r="10" fill="#1e293b" />
      <circle cx={cx} cy={cy} r="5"  fill="white" />
      <text x={cx} y={cy + 32} textAnchor="middle" fontSize="22" fontWeight="900" fill={color} fontFamily="Manrope,sans-serif">{label}</text>
      <text x={cx} y={cy + 46} textAnchor="middle" fontSize="9.5" fill="#94a3b8" fontFamily="Inter,sans-serif">{subLabel}</text>
    </svg>
  );
};

// ── Motorbike-style speedometer gauge ────────────────────────────────────────
const GaugeMeter = ({ plan }) => {
  const used = plan.used_kg || 0;
  const total = plan.total_kg || 1;
  const remaining = Math.max(0, total - used);
  const pct = remaining / total;
  const color = pct > 0.6 ? '#10B981' : pct > 0.3 ? '#F97316' : '#EF4444';
  return (
    <div className="flex flex-col items-center">
      <GaugeSVG pct={pct} label={remaining.toFixed(1)} subLabel="kg remaining" />
      <div className="flex items-center justify-between w-full text-xs px-4 mt-2">
        <div className="text-center">
          <p className="font-black text-slate-700">{used.toFixed(1)} kg</p>
          <p className="text-slate-400">used</p>
        </div>
        <div className="h-8 w-px bg-slate-100" />
        <div className="text-center">
          <p className="font-black text-slate-700">{total} kg</p>
          <p className="text-slate-400">plan size</p>
        </div>
        <div className="h-8 w-px bg-slate-100" />
        <div className="text-center">
          <p className="font-black" style={{ color }}>{Math.round(pct * 100)}%</p>
          <p className="text-slate-400">left</p>
        </div>
      </div>
    </div>
  );
};

// ── Expiry date gauge ─────────────────────────────────────────────────────────
const ExpiryGaugeMeter = ({ plan }) => {
  const expiry = new Date(plan.expiry_date);
  const now = new Date();
  const msPerDay = 1000 * 60 * 60 * 24;
  const daysRemaining = Math.max(0, Math.ceil((expiry - now) / msPerDay));
  // total plan duration: derive from kg size or fallback
  const totalDays = plan.total_kg <= 10 ? 30 : plan.total_kg <= 20 ? 60 : 90;
  const pct = Math.min(1, daysRemaining / totalDays);
  const color = pct > 0.6 ? '#10B981' : pct > 0.3 ? '#F97316' : '#EF4444';
  const expiryStr = expiry.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  return (
    <div className="flex flex-col items-center">
      <GaugeSVG pct={pct} label={String(daysRemaining)} subLabel="days left" colorOverride={color} />
      <div className="flex items-center justify-between w-full text-xs px-4 mt-2">
        <div className="text-center">
          <p className="font-black text-slate-700">{daysRemaining}</p>
          <p className="text-slate-400">days left</p>
        </div>
        <div className="h-8 w-px bg-slate-100" />
        <div className="text-center">
          <p className="font-black text-slate-700">{totalDays}</p>
          <p className="text-slate-400">total days</p>
        </div>
        <div className="h-8 w-px bg-slate-100" />
        <div className="text-center">
          <p className="font-black" style={{ color }}>{Math.round(pct * 100)}%</p>
          <p className="text-slate-400">remaining</p>
        </div>
      </div>
      <p className="text-[11px] text-slate-400 mt-1.5">Expires {expiryStr}</p>
    </div>
  );
};

// ── Order History with animations ─────────────────────────────────────────────
const STATUS_ICON = {
  pending:          { icon: Clock,        bg: 'bg-amber-50',   color: 'text-amber-500' },
  pickup_assigned:  { icon: Truck,        bg: 'bg-blue-50',    color: 'text-blue-500' },
  pickup_done:      { icon: Package,      bg: 'bg-indigo-50',  color: 'text-indigo-500' },
  in_process:       { icon: Cog,          bg: 'bg-violet-50',  color: 'text-violet-500' },
  process_complete: { icon: CheckCircle,  bg: 'bg-teal-50',    color: 'text-teal-600' },
  cleaned:          { icon: Sparkles,     bg: 'bg-emerald-50', color: 'text-emerald-600' },
  out_for_delivery: { icon: Truck,        bg: 'bg-orange-50',  color: 'text-orange-500' },
  delivered:        { icon: CheckCircle,  bg: 'bg-green-50',   color: 'text-green-500' },
  delivery_overdue: { icon: AlertCircle,  bg: 'bg-red-50',     color: 'text-red-500' },
};

const OrderHistoryCard = ({ order, index, visible, isRated, onRate, onPayNow, onViewInvoice }) => {
  const cfg = STATUS_ICON[order.status] || STATUS_ICON.pending;
  const Icon = cfg.icon;
  const label = order.total_clothes != null
    ? `${order.total_clothes} item${order.total_clothes !== 1 ? 's' : ''}`
    : order.service_summary || 'Laundry order';
  const isDelivered = order.status === 'delivered';
  const showRateBadge = isDelivered && isRated === false;
  const showRatedBadge = isDelivered && isRated === true;
  const isPaid = order.payment_status === 'paid';
  const hasAmount = order.total_amount > 0;

  return (
    <div
      className="px-5 py-4 hover:bg-slate-50/80 transition-all duration-300 group"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(14px)',
        transition: `opacity 350ms ease ${index * 60}ms, transform 350ms ease ${index * 60}ms`,
      }}
    >
      <div className="flex items-center gap-3.5">
        <div className={`w-10 h-10 ${cfg.bg} rounded-xl flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-105`}>
          <Icon className={`w-4.5 h-4.5 ${cfg.color}`} strokeWidth={2} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-slate-800 truncate">{label}</p>
          <div className="flex items-center gap-2 mt-0.5">
            <p className="text-xs text-slate-400">{format(new Date(order.order_date), 'dd MMM yyyy')}</p>
            {order.actual_kg != null && (
              <span className="text-[10px] font-semibold text-slate-400">· {order.actual_kg} kg</span>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
          <span className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full border ${STATUS_BADGE[order.status] || 'bg-slate-50 text-slate-600 border-slate-200'}`}>
            {order.status.replace('_', ' ')}
          </span>
          {/* Payment badge */}
          {hasAmount && (
            isPaid ? (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                Paid
              </span>
            ) : (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-200">
                Unpaid
              </span>
            )
          )}
          {showRateBadge && (
            <button onClick={() => onRate(order)}
              className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-200 flex items-center gap-0.5 hover:bg-amber-100 transition-colors">
              <Star className="w-2.5 h-2.5 fill-amber-400 text-amber-400" /> Rate
            </button>
          )}
          {showRatedBadge && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-200 flex items-center gap-0.5">
              <Star className="w-2.5 h-2.5 fill-emerald-400 text-emerald-400" /> Rated
            </span>
          )}
          {isDelivered && order.delivery_date && !showRateBadge && !showRatedBadge && (
            <p className="text-[10px] text-emerald-500 font-medium">
              {format(new Date(order.delivery_date), 'dd MMM')}
            </p>
          )}
        </div>
      </div>
      {/* Invoice / Pay row */}
      {hasAmount && (
        <div className="flex items-center gap-2 mt-2 ml-[52px]">
          {!isPaid && ['process_complete', 'cleaned', 'out_for_delivery', 'delivered'].includes(order.status) && (
            <button onClick={() => onPayNow(order)}
              className="flex items-center gap-1 text-[10px] font-bold text-white bg-blue-600 hover:bg-blue-700 px-2.5 py-1 rounded-lg transition-colors">
              <CreditCard className="w-3 h-3" /> Pay Now
            </button>
          )}
          <button onClick={() => onViewInvoice(order)}
            className="flex items-center gap-1 text-[10px] font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 px-2.5 py-1 rounded-lg transition-colors">
            <Receipt className="w-3 h-3" /> Invoice
          </button>
          {hasAmount && <span className="text-[10px] text-slate-400 ml-auto">₹{order.total_amount?.toFixed(0)}</span>}
        </div>
      )}
    </div>
  );
};

const OrderHistory = ({ orders, onViewAll, ratingMap, onRate, onPayNow, onViewInvoice }) => {
  const [expanded, setExpanded] = useState(false);
  const [visible, setVisible] = useState(false);
  const [expandVisible, setExpandVisible] = useState(false);
  const sectionRef = useRef(null);

  // Trigger entrance animation when section scrolls into view
  useEffect(() => {
    if (!orders.length) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.12 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, [orders.length]);

  // Animate newly revealed cards after expansion
  useEffect(() => {
    if (expanded) {
      setExpandVisible(false);
      const t = setTimeout(() => setExpandVisible(true), 30);
      return () => clearTimeout(t);
    }
  }, [expanded]);

  if (!orders.length) return null;

  const initial = orders.slice(0, 3);
  const extra = orders.slice(3);
  const hasMore = extra.length > 0;

  return (
    <section ref={sectionRef} className="bg-white border border-slate-200/80 rounded-[2rem] overflow-hidden shadow-sm">
      {/* Header */}
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-slate-100 rounded-xl flex items-center justify-center">
            <History className="w-4 h-4 text-slate-500" />
          </div>
          <div>
            <h3 className="font-black text-slate-800 text-sm leading-none" style={{ fontFamily: 'Manrope, sans-serif' }}>Order History</h3>
            <p className="text-[10px] text-slate-400 mt-0.5">{orders.length} order{orders.length !== 1 ? 's' : ''} total</p>
          </div>
        </div>
        <button onClick={onViewAll}
          className="text-xs font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-0.5 transition-colors group">
          Full history <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
        </button>
      </div>

      {/* Initial 3 cards */}
      <div className="divide-y divide-slate-50/80">
        {initial.map((order, i) => (
          <OrderHistoryCard key={order.id} order={order} index={i} visible={visible} isRated={ratingMap[order.id]} onRate={onRate} onPayNow={onPayNow} onViewInvoice={onViewInvoice} />
        ))}
      </div>

      {/* Expandable extra cards */}
      {hasMore && (
        <>
          <div
            className="overflow-hidden transition-all duration-500 ease-in-out"
            style={{ maxHeight: expanded ? `${extra.length * 80}px` : '0px' }}
          >
            <div className="divide-y divide-slate-50/80">
              {extra.map((order, i) => (
                <OrderHistoryCard key={order.id} order={order} index={i} visible={expandVisible} isRated={ratingMap[order.id]} onRate={onRate} onPayNow={onPayNow} onViewInvoice={onViewInvoice} />
              ))}
            </div>
          </div>

          {/* See More / See Less toggle */}
          <button
            onClick={() => setExpanded(e => !e)}
            className="w-full flex items-center justify-center gap-1.5 py-3.5 text-xs font-bold text-slate-500 hover:text-blue-600 hover:bg-blue-50/50 border-t border-slate-50 transition-all group"
          >
            <ChevronDown
              className={`w-4 h-4 transition-transform duration-300 group-hover:text-blue-500 ${expanded ? 'rotate-180' : ''}`}
            />
            {expanded ? 'See less' : `See ${extra.length} more order${extra.length !== 1 ? 's' : ''}`}
          </button>
        </>
      )}
    </section>
  );
};

// ── Rating components ─────────────────────────────────────────────────────────
const StarSelector = ({ value, onChange, label }) => (
  <div className="space-y-1.5">
    <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest">{label}</p>
    <div className="flex items-center gap-2">
      {[1,2,3,4,5].map(n => (
        <button key={n} type="button" onClick={() => onChange(n)} className="transition-transform hover:scale-110 active:scale-95">
          <Star className={`w-8 h-8 ${n <= value ? 'text-amber-400 fill-amber-400' : 'text-slate-200 fill-slate-100'}`} strokeWidth={1.5} />
        </button>
      ))}
      {value > 0 && <span className="text-sm font-bold text-amber-600 ml-1">{value}/5</span>}
    </div>
  </div>
);

const RatingModal = ({ order, token: t, onClose, onDone }) => {
  const [deliveryRating, setDeliveryRating] = useState(0);
  const [satisfactionRating, setSatisfactionRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const handleSubmit = async () => {
    if (!deliveryRating || !satisfactionRating) { toast.error('Please rate both delivery and satisfaction'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/individual/orders/${order.id}/rate`,
        { delivery_rating: deliveryRating, satisfaction_rating: satisfactionRating, comment: comment || null },
        { headers: { Authorization: `Bearer ${t}` } }
      );
      toast.success('Thank you for your feedback!');
      onDone(order.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit rating');
    } finally { setSubmitting(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm rounded-3xl">
        <DialogHeader>
          <DialogTitle className="font-black text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Rate Your Order</DialogTitle>
        </DialogHeader>
        <div className="space-y-5 py-1">
          {/* Order context */}
          <div className="bg-emerald-50 border border-emerald-100 rounded-2xl px-4 py-3 flex items-center gap-3">
            <div className="w-9 h-9 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-black text-emerald-700 uppercase tracking-widest">Delivered</p>
              <p className="text-sm font-bold text-slate-800 truncate">
                {order.total_clothes != null ? `${order.total_clothes} items` : order.service_summary || 'Laundry order'}
              </p>
              <p className="text-xs text-slate-400">
                Ordered {format(new Date(order.order_date), 'dd MMM yyyy')}
                {order.pickup_time_slot ? ` · Pickup ${order.pickup_time_slot}` : ''}
              </p>
            </div>
          </div>
          <p className="text-xs text-slate-400">How did we do? Your feedback helps us improve.</p>
          <StarSelector value={deliveryRating} onChange={setDeliveryRating} label="Delivery Experience" />
          <StarSelector value={satisfactionRating} onChange={setSatisfactionRating} label="Order Satisfaction" />
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-widest block mb-1.5">Comment (optional)</label>
            <Textarea value={comment} onChange={e => setComment(e.target.value)} placeholder="Any additional feedback..." className="min-h-[70px] rounded-xl border-slate-200 bg-slate-50 text-sm" />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-semibold text-sm hover:bg-slate-50">Skip</button>
          <button onClick={handleSubmit} disabled={submitting}
            className="flex-1 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white font-bold text-sm shadow-md shadow-amber-200 transition-all flex items-center justify-center gap-1.5">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Star className="w-4 h-4 fill-white" />} Submit
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const IndividualDashboard = () => {
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unreadMsg, setUnreadMsg] = useState(0);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [activePlan, setActivePlan] = useState(null);
  const [ratingOrder, setRatingOrder] = useState(null);
  const [ratingMap, setRatingMap] = useState({});
  const [paymentOrder, setPaymentOrder] = useState(null);
  const [invoiceOrder, setInvoiceOrder] = useState(null);
  const dismissedRatings = useRef(new Set()); // track skipped orders so popup doesn't re-appear
  const token = localStorage.getItem('individualToken');
  const [referralData, setReferralData] = useState(null);
  const [codeCopied, setCodeCopied] = useState(false);

  const fetchData = useCallback(async () => {
    if (!token) { navigate('/individual/login'); return; }
    try {
      const [dashRes, chatRes, notifRes] = await Promise.all([
        axios.get(`${API}/individual/dashboard`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/individual/chat/unread-count`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/individual/notifications/unread-count`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      const data = dashRes.data;
      setDashboard(data);
      setCustomer({ name: data.customer_name });
      setUnreadMsg(chatRes.data.count);
      setUnreadNotifications(notifRes.data.count || 0);

      // Check ratings for delivered orders
      const deliveredOrders = (data.recent_orders || []).filter(o => o.status === 'delivered');
      if (deliveredOrders.length > 0) {
        const ratingChecks = await Promise.all(
          deliveredOrders.map(o =>
            axios.get(`${API}/individual/orders/${o.id}/rating`, { headers: { Authorization: `Bearer ${token}` } })
              .then(r => ({ id: o.id, rated: !!r.data?.delivery_rating }))
              .catch(() => ({ id: o.id, rated: false }))
          )
        );
        const map = {};
        ratingChecks.forEach(({ id, rated }) => { map[id] = rated; });
        setRatingMap(map);
        // Auto-popup: only the MOST RECENT unrated delivered order, only if delivered within 48h, never re-show dismissed
        const cutoff = Date.now() - 48 * 60 * 60 * 1000;
        const firstUnrated = deliveredOrders.find(o =>
          !map[o.id] &&
          !dismissedRatings.current.has(o.id) &&
          o.delivery_date && new Date(o.delivery_date).getTime() > cutoff
        );
        if (firstUnrated) setRatingOrder(prev => prev ? prev : firstUnrated);
      }
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem('individualToken');
        localStorage.removeItem('individualData');
        navigate('/individual/login');
      } else {
        toast.error('Failed to load dashboard');
      }
    } finally { setLoading(false); }
  }, [token, navigate]);

  useEffect(() => {
    fetchData();
    const iv = setInterval(fetchData, 30000);
    return () => clearInterval(iv);
  }, [fetchData]);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/individual/plans/active`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setActivePlan(r.data.plan))
      .catch(() => {});
    axios.get(`${API}/individual/referrals`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setReferralData(r.data))
      .catch(() => {});
  }, [token]);

  const handleLogout = () => {
    localStorage.removeItem('individualToken');
    localStorage.removeItem('individualData');
    navigate('/individual/login');
  };

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="flex flex-col items-center gap-3">
        <div className="w-12 h-12 bg-blue-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-200 animate-pulse">
          <img src="/justfold-logo-transparent.png" alt="" className="w-7 h-7" style={{ filter: 'brightness(0) invert(1)' }} />
        </div>
        <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
      </div>
    </div>
  );

  const activeOrder = dashboard?.active_order;
  const recentOrders = dashboard?.recent_orders || [];
  const activeIdx = activeOrder ? STATUS_ORDER.indexOf(activeOrder.status) : -1;

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden" style={{ fontFamily: 'Inter, sans-serif' }}>
      <IndividualSidebar unreadMsg={unreadMsg} onLogout={handleLogout} />

      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile topbar */}
        <header className="md:hidden bg-white border-b border-slate-200 px-4 py-2.5 flex items-center justify-between flex-shrink-0 z-10">
          <button onClick={() => navigate('/')} className="hover:opacity-80 transition-opacity">
            <img src="/logo.png" alt="JustFold" className="h-8 w-auto object-contain" />
          </button>
          <button onClick={() => navigate('/individual/notifications')}
            className="relative w-9 h-9 rounded-xl bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600 transition-colors">
            <Bell className="w-5 h-5" />
            {unreadNotifications > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                {unreadNotifications > 9 ? '9+' : unreadNotifications}
              </span>
            )}
          </button>
        </header>

        <main className="flex-1 overflow-y-auto">
          <div className="max-w-3xl mx-auto px-4 md:px-8 py-6 pb-24 md:pb-8 space-y-5">

            {/* Welcome Banner */}
            <header className="relative bg-gradient-to-br from-orange-500 via-orange-600 to-blue-600 rounded-[2rem] px-8 py-8 text-white overflow-hidden shadow-xl shadow-orange-200">
              <div className="absolute -right-8 -top-8 w-44 h-44 bg-white/10 rounded-full" />
              <div className="absolute right-16 bottom-0 w-24 h-24 bg-white/5 rounded-full" />
              <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)', backgroundSize: '24px 24px' }} />
              <div className="relative z-10">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-orange-200 text-sm font-medium">{greeting()}</span>
                  <span className="text-[10px] bg-white/20 text-white px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">Individual</span>
                </div>
                <h1 className="text-3xl md:text-4xl font-black flex items-center gap-3 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {customer?.name?.split(' ')[0]}! <span className="text-3xl">👋</span>
                </h1>
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-orange-200 rounded-full" />
                  <p className="text-orange-100 font-medium text-sm">72-hour doorstep delivery</p>
                </div>
              </div>
            </header>

            {/* 10% First-Order Discount Banner — shown to referred customers who haven't ordered yet */}
            {referralData?.first_order_discount_pending && (
              <div className="bg-gradient-to-r from-violet-500 to-violet-600 rounded-[2rem] px-6 py-5 text-white shadow-lg shadow-violet-200 relative overflow-hidden">
                <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
                <div className="relative z-10 flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center flex-shrink-0 text-2xl">🎁</div>
                  <div className="flex-1">
                    <p className="font-black text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>25% off your first order!</p>
                    <p className="text-violet-100 text-sm mt-0.5">
                      You were referred by <span className="font-bold text-white">{referralData.referrer_name || referralData.referred_by_code}</span>. Your welcome discount is waiting — it applies automatically when you place your first order.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Referral Code Card — share your own code */}
            {referralData?.referral_code && (
              <div className="bg-white border border-orange-100 rounded-[2rem] px-5 py-4 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 bg-orange-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Gift className="w-4 h-4 text-orange-500" />
                  </div>
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Your Referral Code</p>
                </div>
                <p className="text-2xl font-black text-orange-600 tracking-[0.15em] font-mono text-center bg-orange-50 rounded-xl py-3 mb-2">
                  {referralData.referral_code}
                </p>
                <p className="text-[11px] text-slate-400 text-center mb-3">Friends get 25% off · You get 25% back</p>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(referralData.referral_code);
                      setCodeCopied(true);
                      setTimeout(() => setCodeCopied(false), 2000);
                    }}
                    className="flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold text-sm transition-all"
                  >
                    {codeCopied ? <><Check className="w-4 h-4 text-emerald-600" /><span className="text-emerald-600">Copied!</span></> : <><Copy className="w-4 h-4" /><span>Copy</span></>}
                  </button>
                  <button
                    onClick={() => {
                      const code = referralData.referral_code;
                      const text = encodeURIComponent(`Hey! I use JustFold for laundry pickup in Gurgaon 🧺\nGet 25% off your first order — sign up with my code:\n👉 ${code}\nRegister here: https://justfold.in/individual/login?ref=${code}`);
                      window.open(`https://wa.me/?text=${text}`, '_blank');
                    }}
                    className="flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-semibold text-sm transition-all"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    <span>Share</span>
                  </button>
                </div>
              </div>
            )}

            {/* Active FoldPass Plan — Gauge Meter */}
            {activePlan && activePlan.status === 'pending_payment' && (
              <section className="bg-white border border-amber-200 rounded-[2rem] px-6 py-5 shadow-sm">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-5 h-5 text-amber-500" />
                  </div>
                  <div>
                    <p className="text-xs font-black text-slate-400 uppercase tracking-widest">FoldPass Plan</p>
                    <h3 className="font-bold text-slate-800">{activePlan.plan_name}</h3>
                  </div>
                  <span className="ml-auto text-[10px] font-bold bg-amber-100 text-amber-700 px-2.5 py-1 rounded-full border border-amber-200">PAYMENT PENDING</span>
                </div>
                <p className="text-sm text-slate-500 mb-3">Your payment is being processed. Once confirmed, your plan will activate automatically.</p>
                <button onClick={() => navigate('/individual/foldpass')} className="w-full py-2.5 rounded-xl bg-orange-500 text-white text-sm font-bold">
                  Complete Payment
                </button>
              </section>
            )}
            {activePlan && activePlan.status === 'active' && (
              <section className="bg-white border border-slate-200/80 rounded-[2rem] px-6 py-5 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs font-black text-slate-400 uppercase tracking-widest">FoldPass Plan</p>
                    <h3 className="font-bold text-slate-800">{activePlan.plan_name}</h3>
                  </div>
                  <span className="ml-auto text-[10px] font-bold bg-emerald-100 text-emerald-700 px-2.5 py-1 rounded-full border border-emerald-200">ACTIVE</span>
                </div>

                <div className="grid grid-cols-2 gap-3 mt-2">
                  <div className="bg-slate-50 rounded-2xl p-3 flex flex-col items-center">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">KG Remaining</p>
                    <GaugeMeter plan={activePlan} />
                  </div>
                  <div className="bg-slate-50 rounded-2xl p-3 flex flex-col items-center">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Days Left</p>
                    <ExpiryGaugeMeter plan={activePlan} />
                  </div>
                </div>
              </section>
            )}

            {/* Active Order / Empty State */}
            {activeOrder ? (
              <section className="bg-white border border-slate-200/80 rounded-[2rem] p-6 shadow-sm">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-base font-black text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>Current Order</h3>
                  <span className={`text-xs font-bold px-3 py-1 rounded-full border ${STATUS_BADGE[activeOrder.status] || 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                    {activeOrder.status.replace('_', ' ').toUpperCase()}
                  </span>
                </div>
                <div className="flex items-center gap-4 mb-6 p-4 bg-slate-50 rounded-2xl">
                  <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Shirt className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-bold text-slate-800">
                      {activeOrder.total_clothes != null ? `${activeOrder.total_clothes} items` : activeOrder.service_summary || 'Order'}
                    </p>
                    <p className="text-xs text-slate-500">
                      {activeOrder.actual_kg != null ? `${activeOrder.actual_kg}kg weighed` : activeOrder.clothes_type || '72-hr delivery'}
                    </p>
                  </div>
                  {['in_process', 'process_complete', 'cleaned', 'out_for_delivery', 'delivered'].includes(activeOrder.status) && activeOrder.expected_delivery_date ? (
                    <div className="ml-auto text-right">
                      <p className="text-[10px] text-slate-400 uppercase tracking-widest">Delivery by</p>
                      <p className="text-xs font-bold text-orange-600">
                        {format(new Date(activeOrder.expected_delivery_date), 'dd MMM, h:mm a')}
                      </p>
                    </div>
                  ) : activeOrder.status !== 'delivered' && (
                    <div className="ml-auto text-right">
                      <p className="text-[10px] text-slate-400 uppercase tracking-widest">Delivery</p>
                      <p className="text-xs text-slate-400 italic">TBD after pickup</p>
                    </div>
                  )}
                </div>
                {/* Progress tracker — scrollable */}
                <div className="overflow-x-auto -mx-2 px-2 pb-1">
                  <div className="relative" style={{ minWidth: `${STATUS_STEPS.length * 56}px` }}>
                    <div className="absolute top-4 left-[4%] right-[4%] h-0.5 bg-white/30" />
                    <div className="absolute top-4 left-[4%] h-0.5 bg-white transition-all duration-700"
                      style={{ width: activeIdx <= 0 ? '0%' : `${(activeIdx / (STATUS_STEPS.length - 1)) * 92}%` }} />
                    <div className="relative flex">
                      {STATUS_STEPS.map(({ key, label, icon: Icon }, i) => {
                        const done = i < activeIdx;
                        const current = i === activeIdx;
                        return (
                          <div key={key} className="flex flex-col items-center gap-1" style={{ width: `${100 / STATUS_STEPS.length}%` }}>
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center z-10 transition-all ${
                              done ? 'bg-white/90 text-blue-600' :
                              current ? 'bg-white text-blue-600 ring-2 ring-white/50 scale-110 shadow-lg' :
                              'bg-white/20 text-white/50'
                            }`}>
                              <Icon className="w-4 h-4" />
                            </div>
                            <span className={`text-[8px] font-semibold text-center leading-tight ${current ? 'text-white' : done ? 'text-white/80' : 'text-white/40'}`}
                              style={{ maxWidth: '42px', wordBreak: 'break-word' }}>
                              {label}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                {activeOrder.status === 'delivery_overdue' && (
                  <div className="mt-4 flex items-center gap-2 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
                    <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                    <p className="text-xs text-red-600 font-medium">Delivery is overdue. Contact support for an update.</p>
                  </div>
                )}

                {/* Service Timelines */}
                {activeOrder.service_timelines?.length > 0 && (
                  <div className="mt-4 bg-blue-50 border border-blue-100 rounded-2xl p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-7 h-7 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Clock3 className="w-3.5 h-3.5 text-blue-600" />
                      </div>
                      <span className="text-xs font-black text-slate-600 uppercase tracking-widest">Service Timelines</span>
                    </div>
                    <div className="space-y-2">
                      {activeOrder.service_timelines.map((tl) => {
                        const isPast = new Date(tl.due_date) < new Date();
                        const isDelivered = activeOrder.status === 'delivered';
                        return (
                          <div key={tl.service} className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${isDelivered ? 'bg-emerald-500' : isPast ? 'bg-red-400' : 'bg-blue-400'}`} />
                              <span className="text-xs font-semibold text-slate-700">{tl.label}</span>
                            </div>
                            <div className="text-right">
                              <span className={`text-xs font-bold ${isDelivered ? 'text-emerald-600' : isPast ? 'text-red-500' : 'text-blue-600'}`}>
                                {tl.days} {tl.days === 1 ? 'day' : 'days'}
                              </span>
                              <span className="text-[11px] text-slate-400 ml-1">
                                by {format(new Date(tl.due_date), 'dd MMM, h:mm a')}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </section>
            ) : (
              <section className="bg-white border border-slate-200/80 rounded-[2rem] py-16 px-8 flex flex-col items-center justify-center text-center shadow-sm space-y-5">
                <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center text-slate-300">
                  <Package className="w-12 h-12" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-lg font-semibold text-slate-500 mb-1">No active orders</p>
                  <p className="text-sm text-slate-400">Place an order and we'll pick up within 24 hours</p>
                </div>
                <button onClick={() => navigate('/individual/new-order')}
                  className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-8 py-4 rounded-2xl shadow-md shadow-orange-200 transition-all hover:-translate-y-0.5">
                  <Plus className="w-5 h-5" /> Place New Order
                </button>
              </section>
            )}

            {activeOrder && (
              <button onClick={() => navigate('/individual/new-order')} disabled
                className="w-full flex items-center justify-center gap-2 bg-slate-100 text-slate-500 font-semibold py-3.5 rounded-2xl text-sm cursor-not-allowed">
                <Clock className="w-4 h-4" /> Order in progress — new order available after delivery
              </button>
            )}

            {/* Order History */}
            <OrderHistory
              orders={recentOrders}
              onViewAll={() => navigate('/individual/orders')}
              ratingMap={ratingMap}
              onRate={(order) => setRatingOrder(order)}
              onPayNow={(order) => setPaymentOrder(order)}
              onViewInvoice={(order) => setInvoiceOrder(order)}
            />

            {/* Support Grid */}
            <div className="space-y-3">
              <button onClick={() => navigate('/individual/chat')}
                className="relative bg-white border border-slate-200/80 rounded-2xl p-4 flex items-center gap-3 w-full hover:bg-slate-50 hover:border-blue-200 hover:shadow-md transition-all group shadow-sm">
                {unreadMsg > 0 && (
                  <span className="absolute top-3 right-3 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">{unreadMsg}</span>
                )}
                <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center group-hover:bg-blue-100 transition-colors flex-shrink-0">
                  <MessageCircle className="w-5 h-5 text-blue-600" />
                </div>
                <span className="text-sm font-semibold text-slate-700">Chat Support</span>
              </button>
              <div className="bg-white border border-slate-200/80 rounded-2xl px-5 py-4 shadow-sm space-y-3">
                <p className="text-xs font-black text-slate-400 uppercase tracking-widest">Contact Us</p>
                <a href="tel:+918384082073" className="flex items-center justify-between hover:bg-slate-50 rounded-xl px-2 py-1.5 transition-colors">
                  <div>
                    <p className="text-xs font-bold text-slate-700">Placing Order</p>
                    <p className="text-[11px] text-slate-400">8384082073</p>
                  </div>
                  <Phone className="w-4 h-4 text-green-500" />
                </a>
                <a href="tel:+919311108140" className="flex items-center justify-between hover:bg-slate-50 rounded-xl px-2 py-1.5 transition-colors">
                  <div>
                    <p className="text-xs font-bold text-slate-700">For Enquiry</p>
                    <p className="text-[11px] text-slate-400">9311108140</p>
                  </div>
                  <Phone className="w-4 h-4 text-green-500" />
                </a>
                <a href="tel:+919821381888" className="flex items-center justify-between hover:bg-slate-50 rounded-xl px-2 py-1.5 transition-colors">
                  <div>
                    <p className="text-xs font-bold text-slate-700">Customer Complaints</p>
                    <p className="text-[11px] text-slate-400">9821381888</p>
                  </div>
                  <Phone className="w-4 h-4 text-green-500" />
                </a>
              </div>
            </div>

          </div>
        </main>
      </div>

      <IndividualBottomNav />

      {/* Rating Modal */}
      {ratingOrder && (
        <RatingModal
          order={ratingOrder}
          token={token}
          onClose={() => {
            dismissedRatings.current.add(ratingOrder.id);
            setRatingOrder(null);
          }}
          onDone={(id) => {
            setRatingMap(m => ({ ...m, [id]: true }));
            setRatingOrder(null);
          }}
        />
      )}

      {/* Payment Modal */}
      {paymentOrder && (
        <PaymentModal
          order={paymentOrder}
          token={token}
          onClose={() => { setPaymentOrder(null); fetchData(); }}
        />
      )}

      {/* Invoice Modal */}
      {invoiceOrder && (
        <InvoiceModal
          order={invoiceOrder}
          onClose={() => setInvoiceOrder(null)}
        />
      )}
    </div>
  );
};

export default IndividualDashboard;
